import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import ConsentHistoryDialog from 'components/legal/ConsentHistoryDialog';
import { errorMessage, formatLegalDateTime, platformLabel, unwrapEnvelope } from 'components/legal/legalUtils';
import {
  ConsentReportPage,
  ConsentReportRow,
  ConsentReportStatusFilter,
  ConsentReportTypeFilter,
  ConsentState,
  ConsentType
} from 'types/legal';

const EXPORT_PAGE_SIZE = 5000; // server cap

type CellView = { label: string; color: 'default' | 'success' | 'error' | 'warning'; title?: string };

// null means the consent doesn't apply to this person (checkbox off, a role that can't
// sign in where it's required, or no phone number for SMS).
const cellView = (state: ConsentState | null, type: ConsentType): CellView => {
  if (!state) return { label: 'Not required', color: 'default' };
  const when = formatLegalDateTime(state.at);
  const admin = state.source === 'ADMIN';
  const by = state.recordedBy?.name ? ` by ${state.recordedBy.name}` : '';
  switch (state.status) {
    case 'ACCEPTED': {
      if (admin) return { label: 'Recorded by admin', color: 'success', title: [`Recorded${by}`, when].filter(Boolean).join(' · ') };
      const version = type === 'PRIVACY_TERMS' && state.privacyVersion ? `v${state.privacyVersion}` : '';
      const label = ['Accepted', platformLabel(state.platform), version].filter(Boolean).join(' · ');
      const versions =
        type === 'PRIVACY_TERMS'
          ? [state.privacyVersion && `Privacy Policy v${state.privacyVersion}`, state.termsVersion && `Terms v${state.termsVersion}`]
              .filter(Boolean)
              .join(', ')
          : '';
      return { label, color: 'success', title: [when, versions].filter(Boolean).join(' · ') || undefined };
    }
    case 'WITHDRAWN':
      return {
        label: admin ? 'Withdrawn by admin' : 'Withdrawn',
        color: 'warning',
        title: [admin ? `Recorded${by}` : '', when].filter(Boolean).join(' · ') || undefined
      };
    default:
      return { label: 'Missing', color: 'error' };
  }
};

const csvStatus = (state: ConsentState | null) => (state ? state.status : 'NOT_REQUIRED');

// Quotes every cell and neutralises spreadsheet formulas (=, +, -, @ at the start).
const csvCell = (value: unknown) => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

const toCsv = (rows: ConsentReportRow[]) => {
  const header = [
    'Name',
    'Role',
    'Phone',
    'Last login',
    'Privacy & Terms',
    'Privacy & Terms at',
    'Privacy & Terms platform',
    'Privacy & Terms source',
    'Privacy Policy version',
    'Terms version',
    'Privacy & Terms recorded by',
    'SMS',
    'SMS at',
    'SMS platform',
    'SMS source',
    'SMS recorded by'
  ];
  const lines = rows.map((row) => {
    const pt = row.privacyTerms;
    const sms = row.sms;
    return [
      row.name,
      row.role,
      row.phone,
      row.lastLoginAt,
      csvStatus(pt),
      pt?.at,
      pt?.platform,
      pt?.source,
      pt?.privacyVersion,
      pt?.termsVersion,
      pt?.recordedBy?.name,
      csvStatus(sms),
      sms?.at,
      sms?.platform,
      sms?.source,
      sms?.recordedBy?.name
    ]
      .map(csvCell)
      .join(',');
  });
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};

interface ConsentReportProps {
  clientUid: string;
  clientName: string;
  onBack: () => void;
}

const ConsentReport = ({ clientUid, clientName, onBack }: ConsentReportProps) => {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [type, setType] = useState<ConsentReportTypeFilter>('ALL');
  const [status, setStatus] = useState<ConsentReportStatusFilter>('MISSING');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [result, setResult] = useState<ConsentReportPage>({ rows: [], total: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [historyFor, setHistoryFor] = useState<ConsentReportRow | null>(null);
  const requestId = useRef(0);

  // Every filter change returns to the first page in the same render, so the fetch
  // below runs once per change.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const ticket = ++requestId.current;
    setLoading(true);
    setError(null);
    apiRef.current
      .getConsentReport(clientUid, { type, status, search: debouncedSearch, page: page + 1, limit: rowsPerPage })
      .then((response: any) => {
        if (ticket !== requestId.current) return;
        const data = unwrapEnvelope<ConsentReportPage>(response);
        setResult({
          rows: data?.rows ?? [],
          total: Number(data?.total) || 0,
          page: data?.page ?? page + 1,
          limit: data?.limit ?? rowsPerPage
        });
      })
      .catch((err: any) => {
        if (ticket !== requestId.current) return;
        setError(errorMessage(err, 'Could not load the consent report.'));
        setResult({ rows: [], total: 0, page: 1, limit: rowsPerPage });
      })
      .finally(() => {
        if (ticket === requestId.current) setLoading(false);
      });
  }, [clientUid, type, status, debouncedSearch, page, rowsPerPage]);

  // Pulls every matching row (5000 per request, the server cap) and builds the CSV here.
  const handleExport = async () => {
    setExporting(true);
    try {
      const all: ConsentReportRow[] = [];
      for (let p = 1; ; p++) {
        const data = unwrapEnvelope<ConsentReportPage>(
          await apiRef.current.getConsentReport(clientUid, { type, status, search: debouncedSearch, page: p, limit: EXPORT_PAGE_SIZE })
        );
        const rows = data?.rows ?? [];
        all.push(...rows);
        if (rows.length < EXPORT_PAGE_SIZE || all.length >= (Number(data?.total) || 0)) break;
      }
      const blob = new Blob(['\uFEFF' + toCsv(all)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const safeName = (clientName || 'client')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase();
      anchor.href = url;
      anchor.download = `consent-report-${safeName}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      api.showToast(`Exported ${all.length} ${all.length === 1 ? 'row' : 'rows'}`, 'success');
    } catch (err: any) {
      api.showToast(errorMessage(err, 'Could not export the report.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const renderCell = (state: ConsentState | null, consentType: ConsentType) => {
    const view = cellView(state, consentType);
    const chip = (
      <Chip
        size="small"
        label={view.label}
        color={view.color}
        variant={view.color === 'default' ? 'outlined' : 'filled'}
        sx={view.color === 'default' ? { color: 'text.secondary' } : undefined}
      />
    );
    return view.title ? (
      <Tooltip title={view.title} arrow>
        {chip}
      </Tooltip>
    ) : (
      chip
    );
  };

  const from = result.total === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, result.total);

  return (
    <>
      <MainCard
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" color="secondary" startIcon={<ArrowBackIcon />} onClick={onBack}>
              Back
            </Button>
            <Typography variant="subtitle1" component="span">
              Consent report · {clientName}
            </Typography>
          </Stack>
        }
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="consent-report-type">Checkbox</InputLabel>
            <Select
              labelId="consent-report-type"
              label="Checkbox"
              value={type}
              onChange={(event) => {
                setType(event.target.value as ConsentReportTypeFilter);
                setPage(0);
              }}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="PRIVACY_TERMS">Privacy Policy & Terms</MenuItem>
              <MenuItem value="SMS">SMS notifications</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="consent-report-status">Status</InputLabel>
            <Select
              labelId="consent-report-status"
              label="Status"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as ConsentReportStatusFilter);
                setPage(0);
              }}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="MISSING">Missing</MenuItem>
              <MenuItem value="ACCEPTED">Accepted</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            placeholder="Name or phone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="outlined"
            color="secondary"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={exporting || loading || result.total === 0}
          >
            Export CSV
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table size="small" aria-label={`Consent report for ${clientName}`}>
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Privacy & Terms</TableCell>
                <TableCell>SMS</TableCell>
                <TableCell>Last login</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} aria-label="Loading report" />
                  </TableCell>
                </TableRow>
              ) : result.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nobody matches these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                result.rows.map((row) => (
                  <TableRow key={row.uid} hover>
                    <TableCell data-no-translate="true">
                      <Link component="button" type="button" variant="body2" onClick={() => setHistoryFor(row)} sx={{ textAlign: 'left' }}>
                        {row.name || '—'}
                      </Link>
                    </TableCell>
                    <TableCell>{row.role || '—'}</TableCell>
                    <TableCell data-no-translate="true">{row.phone || '—'}</TableCell>
                    <TableCell>{renderCell(row.privacyTerms, 'PRIVACY_TERMS')}</TableCell>
                    <TableCell>{renderCell(row.sms, 'SMS')}</TableCell>
                    <TableCell>{row.lastLoginAt ? formatLegalDateTime(row.lastLoginAt) : 'Never'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {from}–{to} of {result.total}. Records are read-only. Select a name to see that person's consent history.
          </Typography>
          <TablePagination
            component="div"
            count={result.total}
            page={page}
            onPageChange={(_event, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Stack>
      </MainCard>

      {historyFor && (
        <ConsentHistoryDialog open resourceUid={historyFor.uid} personName={historyFor.name} onClose={() => setHistoryFor(null)} />
      )}
    </>
  );
};

export default ConsentReport;
